import { useRef, useState, useCallback, useEffect, Fragment } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import { ALL_TYPES, isMaterialNumberRequired, MATERIAL_NUMBER_TYPES } from './materialFieldConfig';

// ── Static option lists (same as MasterRequestForm) ──────────────────────────
const MATERIAL_TYPE_OPTIONS = ALL_TYPES;

const MATERIAL_TYPE_CHOICES = MATERIAL_TYPE_OPTIONS.map(code => ({ code }));

// ── Searchable cell dropdown ──────────────────────────────────────────────────
// Renders its option list in a fixed layer so the table's horizontal scroll
// container (overflow: auto) can't clip it, and filters by code or description.
const SearchCell = ({ value, options, onChange, placeholder = 'Search…' }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [rect, setRect] = useState(null);
  const inputRef = useRef(null);

  const label = value
    ? (() => { const o = options.find(x => x.code === value); return o ? `${o.code}${o.description ? ` – ${o.description}` : ''}` : value; })()
    : '';

  const filtered = options.filter(o => {
    const s = q.trim().toUpperCase();
    return !s || o.code.toUpperCase().includes(s) || (o.description || '').toUpperCase().includes(s);
  });

  const openMenu = () => {
    const r = inputRef.current?.getBoundingClientRect();
    if (r) setRect({ top: r.bottom + 2, left: r.left, width: r.width });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = e => {
      if (inputRef.current && inputRef.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest('[data-search-menu]')) return;
      setOpen(false);
    };
    const close = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  return (
    <>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          style={S.cell}
          value={open ? q : label}
          placeholder={placeholder}
          onFocus={() => { setQ(''); openMenu(); }}
          onChange={e => { setQ(e.target.value); if (!open) openMenu(); }}
        />
        {value && (
          <button type="button" style={S.cellClear}
            onMouseDown={e => { e.preventDefault(); onChange(''); setQ(''); }}>
            ✕
          </button>
        )}
      </div>
      {open && rect && (
        <div data-search-menu style={{ ...S.menu, top: rect.top, left: rect.left, width: rect.width }}>
          {filtered.length === 0
            ? <div style={S.menuNone}>No match</div>
            : filtered.map(o => (
              <div key={o.code} style={{ ...S.menuOpt, ...(o.code === value ? S.menuOptActive : null) }}
                onMouseDown={e => { e.preventDefault(); onChange(o.code); setOpen(false); setQ(''); }}>
                <span style={S.menuCode}>{o.code}</span>
                {o.description && <span style={S.menuDesc}>{o.description}</span>}
              </div>
            ))}
        </div>
      )}
    </>
  );
};

const COLUMNS = [
  { key: 'material_description', label: 'Material Description', required: true },
  { key: 'material_type',        label: 'Material Type',        required: true, options: MATERIAL_TYPE_OPTIONS },
  { key: 'material_group',       label: 'Material Group',       required: true },
  { key: 'base_uom',             label: 'Base UOM',             required: true },
];

// ── Download sample Excel ─────────────────────────────────────────────────────
// ExcelJS is used (instead of the SheetJS writer) because it can write real
// Excel list validations, i.e. the dropdowns on Material Type / Group / UOM.
// It is imported on demand so the main bundle stays small.
const SAMPLE_MAX_ROW = 1000;

const addListValidation = (ws, column, label, sheetName, count) => {
  ws.dataValidations.add(`${column}2:${column}${SAMPLE_MAX_ROW}`, {
    type: 'list',
    allowBlank: true,
    showErrorMessage: true,
    errorStyle: 'stop',
    errorTitle: `Invalid ${label}`,
    error: `Please pick a ${label} from the dropdown list.`,
    formulae: [`'${sheetName}'!$A$2:$A$${count + 1}`],
  });
};

const downloadSample = async (materialGroups, uoms) => {
  const { default: ExcelJS } = await import('exceljs');

  const typeList = MATERIAL_TYPE_OPTIONS;
  const groupList = materialGroups.map(g => g.code).filter(Boolean);
  const uomList = uoms.map(u => u.code).filter(Boolean);

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Material Requests');

  ws.columns = [
    { header: 'Material Description', key: 'material_description', width: 36 },
    { header: 'Material Type',        key: 'material_type',        width: 20 },
    { header: 'Material Group',       key: 'material_group',       width: 22 },
    { header: 'Base UOM',             key: 'base_uom',             width: 16 },
  ];

  ws.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
  ws.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0D1A6E' } };
  ws.getRow(1).alignment = { vertical: 'middle' };
  ws.getRow(1).height = 22;

  // Example rows (material types are always in the dropdown, so they are used
  // for the examples; group / UOM fall back to the first available value).
  ws.addRow({
    material_description: 'Carbon Black N330',
    material_type: typeList.includes('ZDRM') ? 'ZDRM' : typeList[0],
    material_group: groupList[0] || (materialGroups[0]?.code ?? 'MAT001'),
    base_uom: uomList[0] || (uoms[0]?.code ?? 'KG'),
  });
  ws.addRow({
    material_description: 'Rubber Compound XY',
    material_type: typeList.includes('ZFG') ? 'ZFG' : typeList[0],
    material_group: groupList[1] || groupList[0] || (materialGroups[1]?.code ?? 'MAT002'),
    base_uom: uomList[1] || uomList[0] || (uoms[1]?.code ?? 'EA'),
  });

  ws.getCell('A1').note = 'Required. Max 40 characters.';

  // Reference sheets — the dropdowns point at column A of these sheets.
  const typeSheet = wb.addWorksheet('Material Types');
  typeSheet.addRow(['Code']);
  typeList.forEach(code => typeSheet.addRow([code]));
  addListValidation(ws, 'B', 'Material Type', 'Material Types', typeList.length);

  const groupSheet = wb.addWorksheet('Material Groups');
  groupSheet.addRow(['Code', 'Description']);
  if (materialGroups.length) materialGroups.forEach(g => groupSheet.addRow([g.code, g.description || '']));
  else groupSheet.addRow(['(load from SAP)', '']);
  if (groupList.length) addListValidation(ws, 'C', 'Material Group', 'Material Groups', groupList.length);

  const uomSheet = wb.addWorksheet('UOMs');
  uomSheet.addRow(['Code', 'Description']);
  if (uoms.length) uoms.forEach(u => uomSheet.addRow([u.code, u.description || '']));
  else [['KG', 'Kilogram'], ['EA', 'Each'], ['L', 'Litre']].forEach(r => uomSheet.addRow(r));
  if (uomList.length) addListValidation(ws, 'D', 'Base UOM', 'UOMs', uomList.length);

  [typeSheet, groupSheet, uomSheet].forEach(sheet => {
    sheet.getRow(1).font = { bold: true };
    sheet.columns[0].width = 22;
    if (sheet.columnCount > 1) sheet.columns[1].width = 40;
  });

  ws.views = [{ state: 'frozen', ySplit: 1 }];

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'Material_Request_Sample.xlsx';
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ── Validate one row ──────────────────────────────────────────────────────────
// Only the values that came in through the Excel are validated here. The
// Material No is filled in after the upload (in the grid), so it is checked
// separately by materialNoError().
const validateRow = (row, materialGroups, uoms, index) => {
  const errors = [];
  const desc = String(row.material_description || '').trim();
  const mtype = String(row.material_type || '').trim().toUpperCase();
  const mgroup = String(row.material_group || '').trim();
  const uom = String(row.base_uom || '').trim().toUpperCase();

  if (!desc) errors.push('Material Description is required');
  else if (desc.length > 40) errors.push('Material Description max 40 chars');

  if (!mtype) errors.push('Material Type is required');
  else if (!MATERIAL_TYPE_OPTIONS.includes(mtype)) errors.push(`Material Type '${mtype}' is invalid`);

  if (!mgroup) errors.push('Material Group is required');
  else if (materialGroups.length && !materialGroups.find(g => g.code === mgroup))
    errors.push(`Material Group '${mgroup}' not found`);

  if (!uom) errors.push('Base UOM is required');
  else if (uoms.length && !uoms.find(u => u.code === uom))
    errors.push(`UOM '${uom}' not found`);

  return errors;
};

// The Material No is entered after the upload and is mandatory only for the
// material types the checker enforces it on.
const materialNoError = row => {
  if (!isMaterialNumberRequired(row.material_type)) return null;
  const mno = String(row.material_number || '').trim();
  if (!mno) return `Material No is required for material type ${String(row.material_type).trim().toUpperCase()}`;
  if (mno.length > 40) return 'Material No max 40 chars';
  return null;
};

// ── Component ─────────────────────────────────────────────────────────────────
const BulkMaterialUpload = ({ materialGroups = [], uoms = [], onSubmitBulk }) => {
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);          // parsed + validated rows
  const [dupResults, setDupResults] = useState({}); // rowIndex -> dupResult
  const [expanded, setExpanded] = useState({});     // rowIndex -> matches panel open
  const [dupChecking, setDupChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sampleBusy, setSampleBusy] = useState(false);

  const handleDownloadSample = async () => {
    setSampleBusy(true);
    try {
      await downloadSample(materialGroups, uoms);
    } catch (err) {
      toast.error('Could not create the sample Excel: ' + err.message);
    } finally {
      setSampleBusy(false);
    }
  };

  const parseFile = useCallback(file => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { defval: '' });
        if (!raw.length) { toast.error('Excel is empty'); return; }

        // Normalise header keys: match by label or key (case-insensitive)
        const labelToKey = {};
        COLUMNS.forEach(c => {
          labelToKey[c.label.toLowerCase()] = c.key;
          labelToKey[c.key.toLowerCase()] = c.key;
        });

        const parsed = raw.map((r, i) => {
          const norm = {};
          Object.entries(r).forEach(([h, v]) => {
            const mapped = labelToKey[h.trim().toLowerCase()];
            if (mapped) norm[mapped] = String(v).trim();
          });
          const errors = validateRow(norm, materialGroups, uoms, i);
          return { ...norm, _errors: errors, _mnoError: materialNoError(norm), _rowNum: i + 2 };
        });

        setRows(parsed);
        setDupResults({});
        setExpanded({});
        const errCount = parsed.filter(r => r._errors.length).length;
        if (errCount) toast.warn(`${errCount} row(s) have validation errors — fix before submitting`);
        else toast.success(`${parsed.length} row(s) loaded — run duplicate check`);
      } catch (err) {
        toast.error('Failed to parse Excel: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  }, [materialGroups, uoms]);

  const handleFile = e => {
    const file = e.target.files[0];
    if (file) parseFile(file);
    e.target.value = '';
  };

  const updateCell = (idx, key, value) => {
    setRows(prev => {
      const next = [...prev];
      const updated = { ...next[idx], [key]: value };
      // The Material No only applies to certain material types — drop it when
      // the type no longer calls for it.
      if (key === 'material_type' && !isMaterialNumberRequired(value)) updated.material_number = '';
      updated._errors = validateRow(updated, materialGroups, uoms, idx);
      updated._mnoError = materialNoError(updated);
      next[idx] = updated;
      return next;
    });
    // Filling the Material No after the upload does not affect the duplicate
    // check, so its result is kept.
    if (key !== 'material_number') {
      setDupResults(prev => { const n = { ...prev }; delete n[idx]; return n; });
      setExpanded(prev => { const n = { ...prev }; delete n[idx]; return n; });
    }
  };

  const removeRow = idx => {
    setRows(prev => prev.filter((_, i) => i !== idx));
    setDupResults(prev => {
      const n = {};
      Object.entries(prev).forEach(([k, v]) => { if (Number(k) !== idx) n[Number(k) > idx ? Number(k) - 1 : k] = v; });
      return n;
    });
    setExpanded(prev => {
      const n = {};
      Object.entries(prev).forEach(([k, v]) => { if (Number(k) !== idx) n[Number(k) > idx ? Number(k) - 1 : k] = v; });
      return n;
    });
  };

  const toggleExpand = idx => setExpanded(prev => ({ ...prev, [idx]: !prev[idx] }));

  const runDupCheck = async () => {
    const valid = rows.filter(r => !r._errors.length);
    if (!valid.length) { toast.error('Fix all validation errors first'); return; }
    setDupChecking(true);
    const results = {};
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (r._errors.length) continue;
      try {
        const { data } = await api.post('/sap/check-duplicate', {
          description: r.material_description,
          uom: r.base_uom || '',
        });
        results[i] = data;
      } catch { results[i] = { status: 'ERROR' }; }
    }
    setDupResults(results);
    setDupChecking(false);
    const blocked = Object.values(results).filter(r => r.status === 'EXACT_DUPLICATE').length;
    if (blocked) toast.error(`${blocked} row(s) are exact duplicates and will be blocked`);
    else toast.success('Duplicate check complete');
  };

  const allValid = rows.length > 0 && rows.every(r => !r._errors.length);
  const materialNoComplete = rows.every(r => !r._mnoError);
  const dupDone = rows.length > 0 && rows.every((_, i) => dupResults[i] !== undefined);
  const canSubmit = allValid && materialNoComplete && dupDone && rows.every((_, i) => dupResults[i]?.can_create !== false);
  // The Material No column only appears once a row needs it.
  const showMaterialNo = rows.some(r => isMaterialNumberRequired(r.material_type));

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
        await onSubmitBulk(rows.map(r => ({
          material_description: r.material_description,
          material_type: r.material_type,
          material_group: r.material_group,
          base_uom: r.base_uom,
          ...(String(r.material_number || '').trim() ? { material_number: String(r.material_number).trim() } : {}),
        })));
      setRows([]);
      setDupResults({});
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={S.wrap}>
      {/* Toolbar */}
      <div style={S.toolbar}>
        <button type="button" style={S.btnDownload} onClick={handleDownloadSample} disabled={sampleBusy}>
          {sampleBusy ? 'Preparing…' : '⬇ Download Sample Excel'}
        </button>
        <button type="button" style={S.btnUpload} onClick={() => fileRef.current.click()}>
          📂 Upload Excel
        </button>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleFile} />
        {rows.length > 0 && (
          <>
            <button type="button" style={S.btnDup} onClick={runDupCheck} disabled={dupChecking || !allValid}>
              {dupChecking ? 'Checking…' : '🔍 Run Duplicate Check'}
            </button>
            <button type="button" style={{ ...S.btnSubmit, opacity: canSubmit && !submitting ? 1 : 0.45 }}
              onClick={handleSubmit} disabled={!canSubmit || submitting}>
              {submitting ? 'Submitting…' : `✔ Submit ${rows.length} Row(s)`}
            </button>
          </>
        )}
      </div>

      {/* Hint */}
      {rows.length === 0 && (
        <div style={S.hint}>
          Download the sample Excel, fill in your materials, then upload it here.<br />
          <strong>Material Type</strong>, <strong>Material Group</strong> and <strong>Base UOM</strong> are dropdowns in the file, filled from the reference sheets.<br />
          After the upload, a <strong>Material No</strong> column appears for the rows whose material type needs it ({MATERIAL_NUMBER_TYPES.join(', ')}).
        </div>
      )}

      {/* Grid */}
      {rows.length > 0 && (
        <div style={{ overflowX: 'auto', marginTop: 12 }}>
          {showMaterialNo && (
            <div style={S.hint}>
              Enter the <strong>Material No</strong> for the rows marked with material type {MATERIAL_NUMBER_TYPES.join(', ')}.
              For all other material types it is shown as <em>Not applicable</em>.
            </div>
          )}
          <table style={S.table}>
            <thead>
              <tr style={S.thead}>
                <th style={S.th}>#</th>
                {COLUMNS.map(c => (
                  <th key={c.key} style={S.th}>
                    {c.label}
                    {c.required && <span style={S.thStar}> *</span>}
                  </th>
                ))}
                {showMaterialNo && (
                  <th style={S.th} title={`Fill the Material No for material types: ${MATERIAL_NUMBER_TYPES.join(', ')}`}>
                    Material No<span style={S.thStar}> *</span>
                  </th>
                )}
                <th style={S.th}>Dup Status</th>
                <th style={S.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const dup = dupResults[i];
                const rowErrors = row._mnoError ? [...row._errors, row._mnoError] : row._errors;
                const hasErr = rowErrors.length > 0;
                const matchCount = dup?.matches?.length || 0;
                const isOpen = !!expanded[i];
                const needsMaterialNo = isMaterialNumberRequired(row.material_type);
                const materialNoMissing = needsMaterialNo && !String(row.material_number || '').trim();
                return (
                  <Fragment key={i}>
                  <tr style={{ ...S.tr, background: hasErr ? '#fff5f5' : dup?.status === 'EXACT_DUPLICATE' ? '#fef2f2' : dup?.status === 'NO_MATCH' ? '#f0fdf4' : '#fff' }}>
                    <td style={{ ...S.td, color: '#94a3b8', fontWeight: 700 }}>{row._rowNum}</td>

                    {/* Material Description */}
                    <td style={S.td}>
                      <input style={{ ...S.cell, borderColor: hasErr && !row.material_description ? '#fca5a5' : '#e2e8f0' }}
                        value={row.material_description || ''}
                        onChange={e => updateCell(i, 'material_description', e.target.value)} />
                    </td>

                    {/* Material Type — searchable dropdown */}
                    <td style={S.td}>
                      <SearchCell
                        value={row.material_type || ''}
                        options={MATERIAL_TYPE_CHOICES}
                        onChange={v => updateCell(i, 'material_type', v)}
                        placeholder="Search type…" />
                    </td>

                    {/* Material Group — searchable dropdown */}
                    <td style={S.td}>
                      <SearchCell
                        value={row.material_group || ''}
                        options={materialGroups}
                        onChange={v => updateCell(i, 'material_group', v)}
                        placeholder="Search group…" />
                    </td>

                    {/* Base UOM — searchable dropdown */}
                    <td style={S.td}>
                      <SearchCell
                        value={row.base_uom || ''}
                        options={uoms}
                        onChange={v => updateCell(i, 'base_uom', v)}
                        placeholder="Search UOM…" />
                    </td>

                    {/* Material No — filled after upload, only for the material types that need it */}
                    {showMaterialNo && (
                      <td style={S.td}>
                        {needsMaterialNo ? (
                          <input style={{ ...S.cell, borderColor: materialNoMissing ? '#fca5a5' : '#e2e8f0' }}
                            value={row.material_number || ''}
                            onChange={e => updateCell(i, 'material_number', e.target.value)}
                            placeholder="Enter Material No" />
                        ) : (
                          <div style={S.naCell}
                            title={row.material_type
                              ? `Material No is not applicable for ${row.material_type}`
                              : 'Select a material type first'}>
                            {row.material_type ? 'Not applicable' : 'Select type…'}
                          </div>
                        )}
                      </td>
                    )}

                    {/* Dup status */}
                    <td style={{ ...S.td, minWidth: 150 }}>
                      {hasErr ? (
                        <span style={S.errBadge} title={rowErrors.join('\n')}>⚠ {rowErrors.length} error(s)</span>
                      ) : !dup ? (
                        <span style={S.pendBadge}>—</span>
                      ) : (
                        <div style={S.dupCell}>
                          {dup.status === 'EXACT_DUPLICATE'
                            ? <span style={S.dupBadge}>❌ Duplicate{matchCount ? ` (${matchCount})` : ''}</span>
                            : dup.status === 'SIMILAR_FOUND'
                              ? <span style={S.simBadge}>⚠ Similar ({matchCount})</span>
                              : <span style={S.okBadge}>✓ OK</span>}
                          {matchCount > 0 && (
                            <button type="button" style={S.detailsBtn} onClick={() => toggleExpand(i)}>
                              {isOpen ? '▴ Hide results' : '▾ View results'}
                            </button>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Remove */}
                    <td style={S.td}>
                      <button type="button" style={S.removeBtn} onClick={() => removeRow(i)}>✕</button>
                    </td>
                  </tr>
                  {isOpen && matchCount > 0 && (
                    <tr style={{ background: '#f8faff' }}>
                      <td colSpan={showMaterialNo ? 8 : 7} style={S.matchesCell}>
                        <div style={S.matchesWrap}>
                          <div style={S.matchesTitle}>
                            {dup.status === 'EXACT_DUPLICATE'
                              ? `Existing material(s) already in SAP — "${row.material_description}" is blocked`
                              : `Similar material(s) found in SAP for "${row.material_description}"`}
                          </div>
                          <table style={S.matchesTable}>
                            <thead>
                              <tr>
                                {['Material', 'Description', 'Similarity', 'SAP UOM', 'Your UOM', 'UOM Match'].map(h => (
                                  <th key={h} style={S.matchTh}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {dup.matches.map(m => (
                                <tr key={m.material}>
                                  <td style={{ ...S.matchTd, fontWeight: 700, color: '#4f6ef7' }}>{m.material}</td>
                                  <td style={S.matchTd}>{m.description}</td>
                                  <td style={{ ...S.matchTd, fontWeight: 700, color: m.similarity >= 95 ? '#dc2626' : '#d97706' }}>{m.similarity}%</td>
                                  <td style={S.matchTd}>{m.sap_uom || '—'}</td>
                                  <td style={S.matchTd}>{m.user_uom || '—'}</td>
                                  <td style={{ ...S.matchTd, fontWeight: 700, color: m.uom_match ? '#4f6ef7' : '#dc2626' }}>{m.uom_match ? '✓' : '✗'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Submit gate message */}
      {rows.length > 0 && !canSubmit && dupDone && (
        <div style={S.gateMsg}>
          {rows.some((_, i) => dupResults[i]?.can_create === false)
            ? '❌ Remove or fix exact-duplicate rows before submitting.'
            : !materialNoComplete
              ? `⚠ Enter the Material No for the rows with material type ${MATERIAL_NUMBER_TYPES.join(', ')}.`
              : '⚠ Fix all validation errors, then run duplicate check.'}
        </div>
      )}
    </div>
  );
};

const S = {
  wrap: { marginTop: 8 },
  toolbar: { display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 12 },
  btnDownload: { padding: '9px 16px', borderRadius: 8, border: '1.5px solid #c3cbe0', background: '#f0f4ff', color: '#1A2C80', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnUpload: { padding: '9px 16px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#1A2C80,#4f6ef7)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnDup: { padding: '9px 16px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#d97706,#f59e0b)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnSubmit: { padding: '9px 20px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#1A2C80,#4f6ef7)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  hint: { background: '#f8faff', border: '1.5px dashed #c3cbe0', borderRadius: 10, padding: '18px 20px', fontSize: 13.5, color: '#4a5568', lineHeight: 1.7 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  thead: { background: 'linear-gradient(135deg,#060d3a,#0d1a6e)' },
  th: { padding: '10px 12px', color: '#F7D506', textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: 0.6, whiteSpace: 'nowrap' },
  thStar: { color: '#fca5a5' },
  tr: { borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' },
  td: { padding: '6px 8px', verticalAlign: 'middle' },
  cell: { width: '100%', padding: '7px 22px 7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 6, fontSize: 13, color: '#0B1554', background: '#f8faff', boxSizing: 'border-box', minWidth: 140, outline: 'none' },
  cellClear: { position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#8892b0', fontSize: 11, padding: '2px 4px', lineHeight: 1 },
  naCell: { width: '100%', padding: '7px 10px', border: '1.5px dashed #cbd5e1', borderRadius: 6, fontSize: 12, color: '#94a3b8', background: '#f8fafc', minWidth: 140, boxSizing: 'border-box', textAlign: 'center' },
  menu: { position: 'fixed', maxHeight: 220, overflowY: 'auto', zIndex: 2000, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 24px rgba(11,21,84,0.15)' },
  menuNone: { padding: '10px 13px', fontSize: 13, color: '#8892b0' },
  menuOpt: { display: 'flex', alignItems: 'baseline', gap: 8, padding: '8px 13px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f0f2f8' },
  menuOptActive: { background: '#f0f4ff' },
  menuCode: { fontWeight: 700, color: '#0B1554', flexShrink: 0 },
  menuDesc: { color: '#4a5568', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  removeBtn: { padding: '5px 10px', borderRadius: 6, border: '1.5px solid #fecaca', background: '#fff5f5', color: '#dc2626', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  errBadge: { fontSize: 11.5, fontWeight: 700, color: '#dc2626', background: '#fef2f2', padding: '3px 8px', borderRadius: 20, cursor: 'help', whiteSpace: 'nowrap' },
  dupBadge: { fontSize: 11.5, fontWeight: 700, color: '#dc2626', background: '#fef2f2', padding: '3px 8px', borderRadius: 20, whiteSpace: 'nowrap' },
  simBadge: { fontSize: 11.5, fontWeight: 700, color: '#b45309', background: '#fffbeb', padding: '3px 8px', borderRadius: 20, whiteSpace: 'nowrap' },
  okBadge: { fontSize: 11.5, fontWeight: 700, color: '#1A2C80', background: '#eef2ff', padding: '3px 8px', borderRadius: 20, whiteSpace: 'nowrap' },
  pendBadge: { fontSize: 11.5, color: '#94a3b8', padding: '3px 8px' },
  dupCell: { display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'flex-start' },
  detailsBtn: { fontSize: 11, fontWeight: 700, color: '#4f6ef7', background: '#f0f4ff', border: '1px solid #c7d2fe', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', whiteSpace: 'nowrap' },
  matchesCell: { padding: '12px 16px', borderBottom: '1px solid #e5e9f5' },
  matchesWrap: { background: '#fff', border: '1px solid #dbe3f5', borderRadius: 10, padding: '12px 14px' },
  matchesTitle: { fontSize: 12, fontWeight: 800, color: '#0B1554', marginBottom: 10 },
  matchesTable: { width: '100%', borderCollapse: 'collapse', fontSize: 12.5 },
  matchTh: { textAlign: 'left', fontSize: 10, fontWeight: 800, color: '#8892b0', letterSpacing: 0.6, textTransform: 'uppercase', padding: '6px 10px', borderBottom: '1px solid #eef1f7' },
  matchTd: { padding: '7px 10px', color: '#374151', borderBottom: '1px solid #f4f6fb' },
  gateMsg: { marginTop: 10, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, color: '#dc2626', fontWeight: 600 },
};

export default BulkMaterialUpload;
